/* eslint-disable max-lines */
/* eslint-disable max-len */
import {
    Box,
    Button,
    HStack,
    MenuItem,
    Spacer,
    Text,
    VStack,
    FormControl,
    Link,
    FormLabel,
} from '@chakra-ui/react';
import { ReportedFindingDataMachine } from '@acp/shared';
import {
    FaIcon,
    RestrictedLengthTextAreaField,
    ThemeTokens,
} from '@acp/core-ui';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { ReportedFindingsMachineContext } from '../machine/context';
import {
    ReportedFindingsWizardRow,
    ReportedFindingsWizardRowHeader,
} from './table/reported-findings-wizard-row';
import { ToggleTooltipFallback } from './table/request-quote-wizard-row';
import { ReportedFindingsWizardSkeleton } from './table/reported-findings-wizard-table';

const informDIFormSchema = z.object({
    notes: z
        .string()
        .trim()
        .min(1, 'Please provide additional information')
        .max(500, 'Message should be 500 characters or less'),
});

function InformDIButton({ reportedFinding }: { reportedFinding: ReportedFindingDataMachine }) {
    const machine = ReportedFindingsMachineContext.useActorRef();
    return (
        <MenuItem
            onClick={() => machine.send({
                type: 'INFORM_DI',
                reportedFindingId: reportedFinding.problemRegister.problemRegisterId,
            })}
        >
            Provide additional information
        </MenuItem>
    );
}

function ToggleActionButton({
    onToggle,
    toggled = false,
    isToggleable = true,
    disabled = false,
}: {
    onToggle: () => void;
    toggled?: boolean;
    isToggleable?: boolean;
    disabled: boolean;
}) {
    // TODO: change wording
    if (!isToggleable) {
        return (
            <ToggleTooltipFallback title="Inapplicable">
                <Text fontSize="small">
                    Detector Inspector does not require additional information.
                </Text>
            </ToggleTooltipFallback>
        );
    }
    if (toggled) {
        return (
            <Button
                onClick={onToggle}
                borderRightWidth={1}
                colorScheme="secondary"
                variant="solid"
                size="sm"
                disabled={disabled}
            >
                Selected
            </Button>
        );
    }

    return (
        <Button
            onClick={onToggle}
            colorScheme="secondary"
            background="white"
            borderRightWidth={1}
            borderRightColor="secondary.900"
            borderColor={ThemeTokens.Defaults.borderColor}
            color="muted.700"
            bgColor="white"
            variant="outline"
            size="sm"
            disabled={disabled}
        >
            Select
        </Button>
    );
}

function InformDISelectItemRow({
    reportedFinding,
    isDisabled,
    isSelected,
}: {
    reportedFinding: ReportedFindingDataMachine;
    isDisabled: boolean;
    isSelected: boolean;
}) {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const { problemRegisterId, canInformDI } = reportedFinding.problemRegister;

    const handleToggle = () => {
        if (!isDisabled) {
            machine.send({
                type: 'TOGGLE_REPORTED_FINDING',
                reportedFindingId: problemRegisterId,
            });
        }
    };

    return (
        <ReportedFindingsWizardRow reportedFinding={reportedFinding}>
            <ToggleActionButton
                onToggle={handleToggle}
                toggled={isSelected}
                isToggleable={canInformDI}
                disabled={isDisabled}
            />
        </ReportedFindingsWizardRow>
    );
}

function InformDISelectItems() {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const selectedItems = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.selectedItems
    );
    const reportedFindings = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.reportedFindings
    );
    const informDIForm = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.informDIForm
    );
    const isLoading = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches({ informDI: 'informDIRequest' })
    );

    const { handleSubmit, control } = useForm<z.infer<typeof informDIFormSchema>>({
        resolver: zodResolver(informDIFormSchema),
        defaultValues: {
            notes: informDIForm.notes,
        },
        mode: 'onChange',
    });

    const isDisabledItemRow = (reportedFinding: ReportedFindingDataMachine) => {
        const { canInformDI } = reportedFinding.problemRegister;

        return !canInformDI;
    };

    const isSelectedItemRow = (reportedFinding: ReportedFindingDataMachine) => (
        selectedItems.includes(reportedFinding.problemRegister.problemRegisterId)
    );

    const onSubmit = (vals: z.infer<typeof informDIFormSchema>) => {
        machine.send({
            type: 'INFORM_DI_CONFIRM',
            informDIForm: {
                notes: vals.notes ?? '',
            },
        });
    };

    return (
        <VStack align="left" w="100%" gap={0} mb={4}>
            <VStack mt={4}>
                <Controller
                    control={control}
                    name="notes"
                    render={({ fieldState }) => (
                        <FormControl isInvalid={fieldState.invalid} isRequired>
                            <FormLabel>Provide information to DI</FormLabel>
                            <RestrictedLengthTextAreaField
                                control={control}
                                name="notes"
                                label=""
                                maxLength={500}
                            />
                        </FormControl>
                    )}
                />
            </VStack>
            <Text fontSize="md" mt={2}>
                The following problems will be resolved by the additional information entered:
            </Text>
            <VStack alignItems="flex-start" spacing={4} mt={8}>
                <Box w="full">
                    <ReportedFindingsWizardRowHeader />
                </Box>

                {reportedFindings == null && <ReportedFindingsWizardSkeleton />}
                <VStack spacing={4} w="full">
                    {reportedFindings?.map((item) => (
                        <InformDISelectItemRow
                            key={`${item.problemRegister.problemRegisterId}`}
                            reportedFinding={item}
                            isDisabled={isDisabledItemRow(item)}
                            isSelected={isSelectedItemRow(item)}
                        />
                    ))}
                </VStack>
            </VStack>
            <HStack justifyContent="end" pt={6}>
                <Button
                    isDisabled={isLoading}
                    onClick={() => machine.send({ type: 'BACK' })}
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                >
                    Back
                </Button>
                <Spacer />
                <Button
                    isLoading={isLoading}
                    colorScheme="secondary"
                    size="md"
                    variant="solid"
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={isLoading || selectedItems.length === 0}
                >
                    <Text>Provide additional information</Text>
                </Button>
            </HStack>
        </VStack>
    );
}

function InformDICompleted() {
    const machine = ReportedFindingsMachineContext.useActorRef();

    return (
        <VStack align="left" w="100%" spacing={4} mt={4}>
            <VStack w="100%" alignItems="center" gap={4} pb={10}>
                <FaIcon w={12} h={12} icon={faCheckCircle} color="success.500" />
                <Text fontSize="xl" fontWeight="medium">
                    Thanks for the additional information
                </Text>
                <Box textAlign="center">
                    <Text fontSize="md" color="muted.700">
                        The Detector Inspector team will review the additional information shortly.
                    </Text>
                </Box>
                <Box textAlign="center" mb={4} mt={2}>
                    <Text fontSize="md" color="muted.700">
                        Great work! You&apos;re on a roll.
                    </Text>
                    <Text fontSize="md" color="muted.700">
                        Want to resolve some more?
                    </Text>
                </Box>
                <Button
                    colorScheme="black"
                    size="md"
                    variant="solid"
                    onClick={() => machine.send({ type: 'RESOLVE_MORE' })}
                >
                    <Text>Continue</Text>
                </Button>

                {/* eslint-disable jsx-a11y/anchor-is-valid */}
                <Link
                    _hover={{ textDecoration: 'underline' }}
                    fontWeight="normal"
                    fontSize="md"
                    color="muted.500"
                    mt={4}
                    onClick={() => machine.send({ type: 'FORCE_CLOSE_MODAL' })}
                >
                    <Text color="muted.700">No thanks, I&apos;m finished</Text>
                </Link>
            </VStack>
        </VStack>
    );
}

export {
    InformDIButton, InformDISelectItems, InformDICompleted,
};
